import React, { useCallback, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { CatalogItemOwnership } from 'fym-shared/src/smartlinks/catalog';
import { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../../../common/types';
import { catalogSongMessages } from '../../catalogSongMessages';
import { formatCatalogDay } from '../../../catalogDates';
import { SongDraftArtwork, SongDraftErrors, SongDraftField, SONG_TITLE_MAX_LENGTH } from '../draft/songPageDraft';
import { SongPageDraftState } from '../draft/useSongPageDraft';
import { songPageMessages } from '../songPageMessages';
import { SongCoverField } from './SongCoverField';
import { SongMusicVideoField } from './SongMusicVideoField';
import { SongMusicVideoChip } from './SongMusicVideoChip';
import { useIsNativeEmbed } from '../../../../../music24/navigation/hooks/useIsNativeEmbed';
import { drawnRemoteSrc } from '../../../../../common/drawnImageSrc';
import { SONG_COVER_FIELD_PX } from './songCoverFieldSize';
import { SongReadOnlyField, SongTextField } from './SongTextField';
import { Panel, PanelBody, PanelHead } from '../../../../../music24/components/primitives/Panel';
import { withBrandWord } from '../../../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from '../SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

const DETAIL_FIELDS: SongDraftField[] = [
  'title',
  'artistName',
  'releaseType',
  'releaseDate',
  'artwork',
  'isrc',
  'ownership',
];

const RELEASE_TYPES = ['Single', 'EP', 'Album'];

/** The sentence of a field error (design b3). */
export function fieldErrorText(intl: IntlShape, errors: SongDraftErrors, field: SongDraftField): string | undefined {
  const error = errors[field];
  if (error === undefined) {
    return undefined;
  }
  if (error === 'required') {
    return intl.formatMessage(songPageMessages.titleRequired);
  }
  if (error === 'tooLong') {
    return intl.formatMessage(songPageMessages.tooLong, { max: SONG_TITLE_MAX_LENGTH });
  }
  return intl.formatMessage(catalogSongMessages.isrcError);
}

interface SongDetailsSectionProps {
  accountId: number;
  item: CatalogItem;
  form: SongPageDraftState;
  readOnly: boolean;
}

/**
 * Details (design b1, b2, b3; owner decision 5). An unreleased song edits its title, artist,
 * type, release date, ISRC and cover art. A claimed song shows the facts of the services read only
 * and changes its ownership and its cover in Music24 (cover editor D8). The demo artist shows everything read only (f7). The changes
 * wait in the save bar of the page.
 */
const SongDetailsSectionComponent: React.FC<SongDetailsSectionProps> = ({ accountId, item, form, readOnly }) => {
  const intl = useIntl();
  const { draft, setField, errors, changed, isUnreleased } = form;
  const unsaved = useMemo(() => changed.filter((field) => DETAIL_FIELDS.includes(field)).length, [changed]);
  const canEditFacts = isUnreleased && !readOnly;
  // The music video field (design music-video g): not in a native app's web view, like the cover
  // editor (`offersCoverEditor`); the apps show their own row.
  const showsMusicVideo = !useIsNativeEmbed();
  const musicVideoField = showsMusicVideo ? (
    <SongMusicVideoField accountId={accountId} item={item} readOnly={readOnly} />
  ) : null;

  const onTitle = useCallback((value: string) => setField('title', value), [setField]);
  const onArtist = useCallback((value: string) => setField('artistName', value), [setField]);
  const onDate = useCallback((value: string) => setField('releaseDate', value), [setField]);
  const onIsrc = useCallback((value: string) => setField('isrc', value), [setField]);
  const onType = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setField('releaseType', event.target.value),
    [setField]
  );
  const onArtwork = useCallback((artwork: SongDraftArtwork | null) => setField('artwork', artwork), [setField]);

  const dateText =
    draft.releaseDate === ''
      ? intl.formatMessage(songPageMessages.eyebrowNoDate)
      : formatCatalogDay(intl, draft.releaseDate);
  const typeText = draft.releaseType === '' ? '—' : draft.releaseType;

  let head: React.ReactNode = null;
  if (!isUnreleased) {
    head = (
      <span className={styles.lockChip}>
        <FontAwesomeIcon icon={faLock} />
        {intl.formatMessage(songPageMessages.fromServices)}
      </span>
    );
  } else if (!readOnly) {
    head =
      unsaved > 0 ? (
        <span className={clsx(styles.saved, styles.unsaved)}>
          {intl.formatMessage(songPageMessages.unsavedCount, { count: unsaved })}
        </span>
      ) : (
        <span className={styles.saved}>
          <FontAwesomeIcon icon={faCheck} />
          {intl.formatMessage(songPageMessages.allSaved)}
        </span>
      );
  }

  return (
    <div className={styles.cols}>
      <Panel>
        <PanelHead
          title={intl.formatMessage(songPageMessages.detailsTitle)}
          right={
            showsMusicVideo ? (
              <span className="d-flex flex-wrap gap-2 align-items-center">
                <SongMusicVideoChip accountId={accountId} item={item} />
                {head}
              </span>
            ) : (
              head
            )
          }
        />
        <PanelBody className={styles.cardBody}>
          {canEditFacts ? (
            <>
              <div className={styles.grid2}>
                <SongTextField
                  label={intl.formatMessage(songPageMessages.fieldTitle)}
                  value={draft.title}
                  onChange={onTitle}
                  error={fieldErrorText(intl, errors, 'title')}
                  maxLength={SONG_TITLE_MAX_LENGTH}
                  testId="song-field-title"
                />
                <SongTextField
                  label={intl.formatMessage(songPageMessages.fieldArtist)}
                  value={draft.artistName}
                  onChange={onArtist}
                  error={fieldErrorText(intl, errors, 'artistName')}
                  maxLength={SONG_TITLE_MAX_LENGTH}
                />
              </div>
              <div className={styles.grid2}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="song-field-type">
                    {intl.formatMessage(songPageMessages.fieldType)}
                  </label>
                  <select id="song-field-type" className={styles.input} value={draft.releaseType} onChange={onType}>
                    <option value="">{intl.formatMessage(songPageMessages.fieldTypeNone)}</option>
                    {/* A type that came from an older form (not one of the three) stays a choice. */}
                    {[...new Set([...RELEASE_TYPES, draft.releaseType])]
                      .filter((type) => type !== '')
                      .map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                  </select>
                </div>
                <SongTextField
                  type="date"
                  label={intl.formatMessage(songPageMessages.fieldReleaseDate)}
                  value={draft.releaseDate}
                  onChange={onDate}
                  help={intl.formatMessage(songPageMessages.fieldReleaseDateHelp)}
                />
              </div>
              <SongTextField
                label={intl.formatMessage(catalogSongMessages.isrc)}
                optionalLabel={intl.formatMessage(catalogSongMessages.optional)}
                value={draft.isrc}
                onChange={onIsrc}
                mono
                placeholder="GB-ABC-26-00042"
                maxLength={20}
                help={intl.formatMessage(songPageMessages.fieldIsrcHelp)}
                error={fieldErrorText(intl, errors, 'isrc')}
              />
              <SongCoverField
                accountId={accountId}
                catalogItemUuid={item.uuid}
                savedArtworkId={item.artworkFile?.id ?? null}
                artwork={draft.artwork}
                isReleased={false}
                readOnly={readOnly}
                onChange={onArtwork}
              />
              {musicVideoField}
            </>
          ) : (
            <>
              <div className={styles.grid2}>
                <SongReadOnlyField label={intl.formatMessage(songPageMessages.fieldTitle)} value={draft.title} />
                <SongReadOnlyField label={intl.formatMessage(songPageMessages.fieldArtist)} value={draft.artistName} />
                <SongReadOnlyField label={intl.formatMessage(songPageMessages.fieldType)} value={typeText} />
                <SongReadOnlyField
                  label={intl.formatMessage(
                    isUnreleased ? songPageMessages.fieldReleaseDate : songPageMessages.fieldReleased
                  )}
                  value={dateText}
                />
                {isUnreleased && draft.isrc !== '' && (
                  <SongReadOnlyField label={intl.formatMessage(catalogSongMessages.isrc)} value={draft.isrc} />
                )}
              </div>
              {!isUnreleased && (
                <span className={styles.help}>
                  {withBrandWord(intl.formatMessage(songPageMessages.fromServicesHelp))}
                </span>
              )}
              {/* A released song keeps the facts of the services, but its cover in Music24 can change (D8). */}
              {!isUnreleased && (
                <SongCoverField
                  accountId={accountId}
                  catalogItemUuid={item.uuid}
                  savedArtworkId={item.artworkFile?.id ?? null}
                  artwork={draft.artwork}
                  storeCoverUrl={item.artworkUrl === null ? null : drawnRemoteSrc(item.artworkUrl, SONG_COVER_FIELD_PX)}
                  isReleased
                  readOnly={readOnly}
                  onChange={onArtwork}
                />
              )}
              {/* A released song gets the same field; the demo artist shows it with its buttons off (design g2). */}
              {musicVideoField}
            </>
          )}
          {!isUnreleased && (
            <>
              <div className={styles.hair} />
              <div className={styles.field}>
                <span className={styles.label}>{intl.formatMessage(songPageMessages.ownership)}</span>
                <div
                  className={styles.segment}
                  role="radiogroup"
                  aria-label={intl.formatMessage(songPageMessages.ownership)}
                >
                  {[CatalogItemOwnership.mine, CatalogItemOwnership.featured].map((ownership) => (
                    <button
                      key={ownership}
                      type="button"
                      role="radio"
                      aria-checked={draft.ownership === ownership}
                      disabled={readOnly}
                      className={clsx(styles.segmentItem, draft.ownership === ownership && styles.segmentOn)}
                      onClick={() => setField('ownership', ownership)}
                    >
                      {intl.formatMessage(
                        ownership === CatalogItemOwnership.mine
                          ? songPageMessages.ownershipMine
                          : songPageMessages.ownershipFeatured
                      )}
                    </button>
                  ))}
                </div>
                <span className={styles.help}>{intl.formatMessage(songPageMessages.ownershipHelp)}</span>
              </div>
            </>
          )}
        </PanelBody>
      </Panel>
      <div className={styles.stack}>
        <Panel>
          <PanelHead title={intl.formatMessage(songPageMessages.aboutTitle)} />
          <PanelBody className={styles.cardBody}>
            <dl className={styles.kv}>
              <dt>{intl.formatMessage(songPageMessages.aboutInCatalog)}</dt>
              <dd>
                {intl.formatMessage(isUnreleased ? songPageMessages.aboutUnreleased : songPageMessages.aboutClaimed)}
              </dd>
              <dt>{intl.formatMessage(songPageMessages.aboutAdded)}</dt>
              <dd>{formatCatalogDay(intl, item.claimedAt)}</dd>
              {isUnreleased && item.releaseDate !== null && (
                <>
                  <dt>{intl.formatMessage(songPageMessages.aboutReleaseDay)}</dt>
                  <dd>
                    {withBrandWord(
                      intl.formatMessage(songPageMessages.aboutReleaseDayValue, {
                        date: formatCatalogDay(intl, item.releaseDate),
                      })
                    )}
                  </dd>
                </>
              )}
            </dl>
          </PanelBody>
        </Panel>
        <div className={clsx(styles.note, styles.noteInfo)}>
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>{intl.formatMessage(songPageMessages.aboutNote, { title: item.title })}</span>
        </div>
      </div>
    </div>
  );
};

export const SongDetailsSection = TypedMemo(SongDetailsSectionComponent);
